<template>
  <div class="toasts">
    <div 
      ref="toastsContainer"
      class="toasts-container"
      @scroll="handleScroll"
    >
      <div 
        class="toasts-placeholder"
        :style="{ height: `${visibleTopHeight}px` }"
      ></div>
      
      <ui-toast-list
        v-for="message in visibleMessages"
        :key="message.id"
      >
        <ui-toast
          :class-name="message.class"
          :text="message.text"
          :title="message.title"
          :icon="message.icon"
          :is-html="message.isHtml"
          :delay="message.delay"
          :start-time="toastTimers[message.id] ? toastTimers[message.id].startTime : null"
          :duration="toastTimers[message.id] ? toastTimers[message.id].duration : null"
          @close="deleteMessage(message.id)"
        />
      </ui-toast-list>
      
      <div 
        class="toasts-placeholder"
        :style="{ height: `${visibleBottomHeight}px` }"
      ></div>
    </div>
    
    <!-- Панель управления уведомлениями -->
    <div class="toasts-control-panel">
      <div class="toasts-counters">
        <span 
          class="toasts-counter-all" 
          :title="`Всего уведомлений: ${totalMessages}`"
        >
          {{ totalMessages }}
        </span>
        <span class="toasts-counter-separator">|</span>
        <span 
          class="toasts-counter-critical" 
          :title="`Критических уведомлений: ${criticalMessagesCount}`"
        >
          {{ criticalMessagesCount }}
        </span>
      </div>
      
      <div class="toasts-control-buttons">
        <button 
          class="toasts-control-button"
          title="Удалить некритические уведомления"
          @click="deleteNonCriticalMessages"
        >
          <img 
            src="/images/notifications/notifications_important.svg" 
            alt="clear non-critical"
            class="toasts-control-icon"
          />
        </button>

        <button 
          class="toasts-control-button"
          title="Удалить все уведомления"
          @click="deleteAllMessages"
        >
          <img 
            src="/images/notifications/notifications.svg" 
            alt="clear all"
            class="toasts-control-icon"
          />
        </button>
        
        <button 
          class="toasts-control-button"
          :title="isToastsVisible ? 'Скрыть уведомления' : 'Показать уведомления'"
          @click="toggleToastsVisibility"
        >
          <img 
            :src="imagePath(isToastsVisible ? '/images/manual/read.png' : '/images/manual/read.png')" 
            alt="toggle"
            class="toasts-control-icon"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import UiToastList from './UIToastList.vue';
import UiToast from './UIToast.vue';

export default {
  name: 'TheToaster',

  components: { UiToastList, UiToast },

  data() {
    return {
      messages: [],
      itemHeight: 194, // Высота одного уведомления 
      itemGap: 10, // Отступ между элементами
      containerHeight: 0,
      scrollTop: 0,
      savedScrollPosition: null, // {type: 'percent'|'id', value: number}
      buffer: 5, // Буфер элементов сверху и снизу
      scrollTimeout: null, // Таймаут для оптимизации обработки скролла
      toastIdCounter: 0, // Счетчик key
      isFirstTime: true, // Первый раз скролим вниз
      isToastsVisible: true, // Флаг отображения уведомлений
      toastTimers: {}, // Отслеживание времени жизни уведомлений
    };
  },

  computed: {
    totalMessages() {
      return this.messages.length;
    },
    
    criticalMessagesCount() {
      return this.messages.filter(message => message.class === 'toast_error').length;
    },
    
    totalHeight() {
      if (this.totalMessages === 0) return 0;
      return this.totalMessages * this.itemHeight + (this.totalMessages - 1) * this.itemGap;
    },
    
    visibleCount() {
      return Math.ceil(this.containerHeight / (this.itemHeight + this.itemGap)) + this.buffer * 2;
    },
    
    startIndex() {
      // Если сообщений мало, показываем все
      if (this.totalMessages <= this.visibleCount) {
        return 0;
      }
      
      const calculatedIndex = Math.floor(this.scrollTop / (this.itemHeight + this.itemGap)) - this.buffer;
      // Убедимся, что startIndex не выходит за пределы возможных значений
      return Math.max(0, Math.min(calculatedIndex, this.totalMessages - 1));
    },
    
    endIndex() {
      // Если сообщений мало, показываем все
      if (this.totalMessages <= this.visibleCount) {
        return this.totalMessages;
      }
      
      const calculatedIndex = this.startIndex + this.visibleCount;
      // Убедимся, что endIndex не выходит за пределы возможных значений
      return Math.min(this.totalMessages, calculatedIndex);
    },
  
    visibleMessages() {
      // Если уведомления скрыты, не показываем их
      if (!this.isToastsVisible) {
        return [];
      }
      return this.messages.slice(this.startIndex, this.endIndex);
    },
    
    visibleTopHeight() {
      // Если сообщений нет, не показываем placeholder
      if (this.totalMessages === 0 || !this.isToastsVisible) {
        return 0;
      }
      // Если сообщений мало, не показываем placeholder
      if (this.totalMessages <= this.visibleCount) {
        return 0;
      }
      // Высота элементов + отступы между ними
      return this.startIndex * this.itemHeight + Math.max(0, this.startIndex - 1) * this.itemGap;
    },
    
    visibleBottomHeight() {
      // Если сообщений нет, не показываем placeholder
      if (this.totalMessages === 0 || !this.isToastsVisible) {
        return 0;
      }
      if (this.totalMessages <= this.visibleCount) {
        return 0;
      }
      const itemsCount = this.totalMessages - this.endIndex;
      const gapsCount = Math.max(0, itemsCount - 1);
      return itemsCount * this.itemHeight + gapsCount * this.itemGap;
    }
  },

  mounted() {
    this.updateContainerHeight();
    this.addResizeListener();
  },

  beforeUnmount() {
    this.removeResizeListener();
    // Очищаем таймаут при удалении компонента
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  },

  watch: {
    messages: {
      handler(newMessages, oldMessages) {
        // Сохраняем текущую позицию перед изменением массива сообщений
        if (oldMessages && oldMessages.length > 0) {
          this.saveScrollPosition(oldMessages);
        }
        
        // Восстанавливаем позицию после изменения массива сообщений
        if (newMessages && newMessages.length > 0) {
          this.$nextTick(() => {
            this.restoreScrollPosition(newMessages);
          });
        } else if (newMessages && newMessages.length === 0) {
          // Если сообщений нет, сбрасываем scrollTop
          this.scrollTop = 0;
          if (this.$refs.toastsContainer) {
            this.$refs.toastsContainer.scrollTop = 0;
          }
        }
        
        if (oldMessages && oldMessages.length > 1000 && newMessages.length < 100) {
          this.scrollTop = 0;
        }
      },
      deep: true
    }
  },

  methods: {
    success({ message = '', delay = 5000, isHtml = false }) {
      this.addMessage({ type: 'success', message, delay, isHtml });
    },
    warning({ message = '', delay = 5000, isHtml = false }) {
      this.addMessage({ type: 'warning', message, delay, isHtml });
    },
    error({ message = '', delay = 5000, isHtml = false }) {
      this.addMessage({ type: 'error', message, delay, isHtml });
    },
    default({ message = '', delay = 5000, isHtml = false }) {
      this.addMessage({ type: 'default', message, delay, isHtml });
    },
    
    toggleToastsVisibility() {
      this.isToastsVisible = !this.isToastsVisible;
    },
    
    deleteAllMessages() {
      this.messages = [];
      this.toastTimers = {};
    },
    
    deleteNonCriticalMessages() {
      this.messages.forEach(message => {
        if (message.class !== 'toast_error' && this.toastTimers[message.id]) {
          delete this.toastTimers[message.id];
        }
      });
      
      this.messages = this.messages.filter(message => message.class === 'toast_error');
    },
    
    imagePath(path) {
      return window.location.origin + path;
    },
    addMessageBatch(messages) {
      const items = [];
      const ids = {};
      for (let i = 0; i < messages.length; i++) {
        const id = ++this.toastIdCounter;
        const { type = 'default', message = '', delay = 5000, isHtml = false, title = null } = messages[i];
        items.push({
          id,
          icon: type,
          class: `toast_${type}`,
          title,
          text: message,
          isHtml: isHtml,
          delay: delay,
        });

        if (delay > 0) {
          this.toastTimers[id] = {
            startTime: Date.now(),
            duration: delay
          };
          
          if (!ids[delay]) ids[delay] = [];
          ids[delay].push(id)
        }
      }

      for (let delay in ids) {
        this.hideMessagesTimerBatch(ids[delay], Number(delay));
      }

      this.messages.push(...items);

      if (this.isFirstTime) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.scrollToBottom();
          }, 50);
        });
        this.isFirstTime = false;
      }
    },
    addMessage({ type = 'default', message = '', delay = 5000, isHtml = false, title = null }) {
      const id = ++this.toastIdCounter;

      this.messages.push({
        id,
        icon: type,
        class: `toast_${type}`,
        title,
        text: message,
        isHtml: isHtml,
        delay: delay,
      });

      if (delay > 0) {
        this.toastTimers[id] = {
          startTime: Date.now(),
          duration: delay
        };
        this.hideMessagesTimer(id, delay);
      }
    },

    hideMessagesTimerBatch(ids, delay) {
      setTimeout(() => {
        this.deleteMessageBatch(ids);
      }, delay);
    },

    hideMessagesTimer(id, delay) {
      setTimeout(() => {
        this.deleteMessage(id);
      }, delay);
    },

    deleteMessageBatch(ids) {
      if (!Array.isArray(ids) || ids.length === 0) return;
      
      const idsSet = new Set(ids); // для быстрого поиска
      
      this.messages = this.messages.filter(message => {
        // Удаляем информацию о времени жизни уведомления
        if (idsSet.has(message.id) && this.toastTimers[message.id]) {
          delete this.toastTimers[message.id];
        }
        return !idsSet.has(message.id);
      });
    },

    deleteMessage(id) {
      const index = this.messages.findIndex(message => message.id === id);
      if (index !== -1) {
        this.messages.splice(index, 1);
        if (this.toastTimers[id]) {
          delete this.toastTimers[id];
        }
      }
    },

    scrollToBottom() {
      if (this.$refs.toastsContainer) {
        const container = this.$refs.toastsContainer;
        try {
          const totalHeight = this.totalMessages * this.itemHeight + Math.max(0, this.totalMessages - 1) * this.itemGap;
          const scrollPosition = Math.max(0, totalHeight - this.containerHeight);
          
          container.scrollTo({
            top: scrollPosition,
            behavior: 'instant'
          });
        } catch (e) {
          container.scrollTop = container.scrollHeight;
        }
      }
    },
    
    handleScroll(event) {
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }
      
      this.scrollTimeout = setTimeout(() => {
        this.scrollTop = event.target.scrollTop;
      }, 50); 
    },
    
    updateContainerHeight() {
      if (this.$refs && this.$refs.toastsContainer) {
        this.containerHeight = this.$refs.toastsContainer.clientHeight;
      }
    },
    
    addResizeListener() {
      this.resizeObserver = new ResizeObserver(this.updateContainerHeight);
      this.resizeObserver.observe(this.$refs.toastsContainer);
    },
    
    removeResizeListener() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    },
    
    saveScrollPosition(messages) {
      if (!this.$refs.toastsContainer) return;
      
      const container = this.$refs.toastsContainer;
      const totalMessages = messages.length;
      const totalHeight = totalMessages === 0 ? 0 : 
        totalMessages * this.itemHeight + (totalMessages - 1) * this.itemGap;
      
      const scrollPercent = totalHeight > 0 && this.containerHeight > 0 ? 
        container.scrollTop / Math.max(1, totalHeight - this.containerHeight) : 0;
      
      this.savedScrollPosition = {
        type: 'percent',
        value: Math.max(0, Math.min(1, scrollPercent))
      };
    },
    
    // Восстановление позиции прокрутки
    restoreScrollPosition(messages) {
      if (!this.$refs.toastsContainer || !this.savedScrollPosition) return;
      
      const container = this.$refs.toastsContainer;
      let scrollTop = 0;
      
      if (this.savedScrollPosition.type === 'percent') {
        const totalHeight = this.totalMessages === 0 ? 0 : 
          this.totalMessages * this.itemHeight + (this.totalMessages - 1) * this.itemGap;
        const maxScrollTop = Math.max(0, totalHeight - this.containerHeight);
        scrollTop = this.savedScrollPosition.value * maxScrollTop;
      }
      
      container.scrollTop = scrollTop;
      this.scrollTop = scrollTop;
      
      this.savedScrollPosition = null;
    }
  },
};
</script>

<style scoped>
.toasts {
  position: fixed;
  bottom: 8px;
  right: 8px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  white-space: pre-wrap;
  z-index: 1002;
}

.toasts-container {
  max-height: calc(100vh - 36px - 31px - 10px - 46px - 10px - 11px);
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-right: 4px;

  -ms-overflow-style: none;
}

.toasts-container::-webkit-scrollbar {
  display: none;
}

.toasts-placeholder {
  flex: 0 0 auto;
}

.toast:not(:last-child) {
  margin-bottom: 0 !important;
}

.toasts-control-panel {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border: 1px solid lightgray;
  border-radius: 4px;
  padding: 8px 12px;
  margin-top: 10px;
}

.toasts-counters {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: bold;
  margin-right: 5px;
}

.toasts-counter-all {
  color: #000000;
}

.toasts-counter-separator {
  margin: 0 6px;
  color: #999999;
}

.toasts-counter-critical {
  color: #e24c4b;
}

.toasts-control-buttons {
  display: flex;
  gap: 8px;
}

.toasts-control-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toasts-control-button:hover {
  background-color: #f0f0f0;
}

.toasts-control-icon {
  width: 20px;
  height: 20px;
}

@media all and (min-width: 992px) {
  .toasts {
    bottom: 8px;
    right: 150px;
  }
  
  .toasts-container {
    max-height: calc(100vh - 36px - 31px - 10px - 46px - 10px - 11px);
  }
}

@media print {
  .toasts * {
    display: none !important;
  }
}
</style>
